// components/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import type { RootState } from '../redux/store';
import ForceChangePasswordModal from '../pages/Auth/ForceChangePassword';

interface ProtectedRouteProps {
  element: React.ReactNode;
  allowedRoles: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element, allowedRoles }) => {
  const { isAuthenticated, user, loading, activeRole } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const sessionToken = localStorage.getItem('sessionToken');

  let effectiveRole = (
    activeRole ||
    user?.role ||
    localStorage.getItem('activeRole') ||
    ''
  );

  if (typeof effectiveRole === 'string' && effectiveRole.includes(',')) {
    effectiveRole = effectiveRole.split(',')[0].trim();
  }

  const roleMatch = allowedRoles
    .map((r) => r.toLowerCase().trim())
    .includes(effectiveRole.toLowerCase().trim());

  const isDefaultPass = user?.isDefaultPass ?? (JSON.parse(localStorage.getItem('userDetails') || '{}')?.isDefaultPass);

  if (sessionToken && loading && !isAuthenticated) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active title paragraph={{ rows: 4 }} />
      </div>
    );
  }

  if (!isAuthenticated && !loading && !sessionToken) {
    return <Navigate to="/login" replace />;
  }

  if (!roleMatch && isAuthenticated) {
    // Platform super-admin lives outside the /v1 tenant space.
    const fallback = effectiveRole.toLowerCase().trim() === 'platform'
      ? '/platform/dashboard'
      : `/v1/${effectiveRole.toLowerCase()}/dashboard`;
    return <Navigate to={fallback} replace />;
  }

  if (isAuthenticated && isDefaultPass) {
    return (
      <>
        <div style={{ filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none', height: '100vh', overflow: 'hidden' }}>
          {element}
        </div>
        <ForceChangePasswordModal />
      </>
    );
  }

  return <>{element}</>;
};

export default ProtectedRoute;
